import React, { useMemo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function AGB() {
  const { t, i18n } = useTranslation()

  const sectionsObj = t('agb.sections', { returnObjects: true }) || {}

  const sections = useMemo(() => {
    const keys = Object.keys(sectionsObj)
    if (!keys.length) return []
    return keys
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => {
        const s = sectionsObj[k] || {}
        const id = slugify(s.title || `section-${k}`)
        return { id, ...s }
      })
  }, [sectionsObj])

  const [activeId, setActiveId] = useState(sections?.[0]?.id)
  const [showTop, setShowTop] = useState(false)

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400)
    onScroll()
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const observers = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(id)),
        { rootMargin: '-40% 0px -55% 0px', threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [sections])

  const changeLang = (lng) => i18n.changeLanguage(lng)

  return (
    <section className="section">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-fuchsia-500/15 via-sky-400/10 to-emerald-400/10 blur-2xl" />
        <div className="relative container mx-auto px-4 pt-10 pb-6 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                {t('agb.title', 'Conditions Générales d’Utilisation')}
              </h1>
              <p className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center rounded-full bg-emerald-600/10 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 text-xs font-medium border border-emerald-600/20">
                  {badgeIcon()}
                  <span className="ml-1">
                    {t('agb.lastUpdated', 'Dernière mise à jour : 2026-01-02')}
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="container mx-auto px-4 pb-16 max-w-6xl">
        {sections.length === 0 ? (
          <div className="rounded-xl border bg-card p-6 text-sm">
            {t('legal.empty', 'Contenu indisponible dans cette langue.')}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8">
            {/* Sommaire */}
            <aside className="lg:sticky lg:top-24 h-max">
              <div className="rounded-2xl border bg-card shadow-sm p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
                  {t('legal.toc', 'Sommaire')}
                </p>
                <nav className="space-y-1">
                  {sections.map((s, i) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className={`block rounded-lg px-3 py-2 text-sm transition
                        ${activeId === s.id
                          ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                          : 'hover:bg-muted text-foreground'
                        }`}
                    >
                      <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                      {stripNumberPrefix(s.title)}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Sections */}
            <main>
              <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-fuchsia-500 via-sky-500 to-emerald-500" />
                <div className="p-6 md:p-8">
                  {sections.map((s) => (
                    <Section key={s.id} section={s} />
                  ))}
                </div>
              </div>
            </main>
          </div>
        )}
      </div>

      {/* Retour haut */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Haut de page"
          className="fixed bottom-6 right-6 rounded-full shadow-lg border bg-background/80 backdrop-blur px-3 py-2 hover:bg-background transition"
        >
          <svg width="22" height="22" viewBox="0 0 24 24">
            <path d="M12 19V5m0 0l-7 7m7-7l7 7" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </section>
  )
}

function Section({ section }) {
  const { id, title, text, list } = section
  return (
    <section id={id} className="scroll-mt-24 border-b last:border-none py-4 border-border/60">
      <details className="group" open>
        <summary className="flex justify-between cursor-pointer px-3 py-2 -mx-3 hover:bg-muted/60 rounded-xl">
          <h2 className="text-lg md:text-xl font-semibold">{stripNumberPrefix(title)}</h2>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border text-muted-foreground group-open:rotate-180 transition">
            <svg width="16" height="16" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" fill="none"/></svg>
          </span>
        </summary>
        <div className="mt-3 space-y-3">
          {text && <p className="leading-relaxed text-[15px] md:text-base">{text}</p>}
          {Array.isArray(list) && list.length > 0 && (
            <ul className="list-disc pl-5 space-y-2 text-[15px] md:text-base">
              {list.map((li, i) => <li key={i}>{li}</li>)}
            </ul>
          )}
        </div>
      </details>
    </section>
  )
}

const slugify = (s = '') => s.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-')
const stripNumberPrefix = (t = '') => t.replace(/^\s*\d+\.\s*/, '')
const badgeIcon = () => <svg width="14" height="14" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.9L18.18 22 12 18.77 5.82 22 7 14.17l-5-4.9 6.91-1.01L12 2z" fill="currentColor"/></svg>
